import { useTranslation } from "react-i18next";
import React, { useEffect } from "react";
import { Form, Button } from "react-bootstrap";
import { useForm, Yup } from "../forms/form";
import { Icon, Title } from "../components";
import { usePropState } from "../utils";
import { useHistory } from "react-router-dom";
import cloneDeep from "lodash/cloneDeep";
import MainForm from "./form/Main";
import PrivacyForm from "./form/Privacy";
import PublisherScopeForm from "./form/PublisherScope";
import WifiForm from "./form/Wifi";
import BillingForm from "./form/Billing";
import OptOutForm from "./form/OptOut";

export default function DataVendorForm(props) {
  const { t } = useTranslation();
  const history = useHistory();
  const [formRefValues] = usePropState(props.formRefValues);

  const initialValues = {
    name: "",
    bucket: "",
    cmp_vendor_id: undefined,
    max_users: "",
    publisher_id: undefined,
    publisher_ids: undefined,
    industry_id: undefined,
    environment: "test",
    terminated_at: undefined,
    billing_start_date: undefined,
    cpm: {},
    flat_fee: "",
    blacklist: undefined,
    isPublisher: (obj) => obj?.isPublisher || !!obj?.publisher_id || false,
    hasPublisherScope: (obj) =>
      obj?.hasPublisherScope || !!obj?.publisher_ids || false,
    billing_model: "",
    with_opt_out: false,
    wifi_network_ssids: [],
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    bucket: Yup.string().required(),
    environment: Yup.string().required(),
    billing_start_date: Yup.date().when("environment", {
      is: "production",
      then: Yup.date().required(),
      otherwise: Yup.date().nullable().oneOf([null]),
    }),
    industry_id: Yup.number().required(),
    publisher_id: Yup.mixed().when("isPublisher", {
      is: true,
      then: Yup.string().required(),
      otherwise: Yup.mixed().nullable().oneOf([null]),
    }),
    cmp_vendor_id: Yup.mixed().when("isPublisher", {
      is: false,
      then: Yup.string().required(),
      otherwise: Yup.mixed().nullable().oneOf([null]),
    }),
    publisher_ids: Yup.mixed().when("hasPublisherScope", {
      is: true,
      then: Yup.array().of(Yup.number()).min(1).required(),
      otherwise: Yup.mixed().nullable().oneOf([null]),
    }),
    blacklist: Yup.boolean().when("hasPublisherScope", {
      is: true,
      then: Yup.boolean().required(),
      otherwise: Yup.boolean().nullable().oneOf([null]),
    }),
    max_users: Yup.string().nullable(),
    billing_model: Yup.string().when("environment", {
      is: "production",
      then: (schema) => schema.required(),
    }),
    flat_fee: Yup.mixed().when("billing_model", {
      is: "flat",
      then: Yup.number().required(),
    }),
    cpm: Yup.mixed().when("billing_model", {
      is: (value) => ["cpm", "cpm_range"].includes(value),
      then: Yup.object()
        .shape({
          0: Yup.number().required(),
        })
        .required(),
    }),
    with_opt_out: Yup.boolean().required(),
    wifi_network_ssids: Yup.array().of(Yup.string()),
  });

  const processedDataForSubmit = (_data) => {
    let data = cloneDeep(_data);
    if (!data.isPublisher) {
      data.publisher_id = null;
    }
    if (!data.hasPublisherScope) {
      data.publisher_ids = null;
      data.blacklist = null;
    }
    if (data.environment === "test") {
      data.billing_start_date = null;
      data.cpm = {};
      data.flat_fee = null;
      data.max_users = null;
    } else if (data.environment === "production") {
      if (data.billing_model === "flat") {
        data.cpm = {};
        data.max_users = null;
      } else {
        data.flat_fee = null;
      }
    }
    return data;
  };

  const { formData, setFormData, getInputProps, formProps, submitButtonProps } =
    useForm({
      defaultObject: props.dataVendor,
      initialValues: initialValues,
      validationSchema: validationSchema,
      onValidation: (formData) => {
        if (formData.environment === "test") {
          setFormData({ ...formData, billing_start_date: null });
        }
      },
      onSubmit: (data) => props.onSubmit(processedDataForSubmit(data)),
      onAttributeChange: {
        name: {
          bucket: (value) => {
            return value
              .toLowerCase()
              .replace(/\s/g, "_")
              .replace(/[^a-z0-9-_]/g, "");
          },
        },
      },
    });

  useEffect(() => {
    setFormData(props.dataVendor);
  }, [props.dataVendor]);

  if (!formRefValues) {
    return <></>;
  }

  const handleCancel = () => {
    history.push({
      pathname: `/data_vendors/${props.dataVendor.slug}`,
      state: { dataVendor: props.dataVendor },
    });
  };

  const childFormProps = {
    getInputProps,
    formRefValues,
    formData,
  };

  return (
    <>
      <Title
        main={props.mainTitle}
        sub={props.subTitle}
        group={false}
        onBack={props.onCancel}
      >
        <Button
          key="submit"
          type="submit"
          variant="primary"
          {...submitButtonProps}
        >
          <Icon icon="save" text={t("save")} />
        </Button>
        {props.dataVendor && (
          <Button variant="secondary" key="cancel" onClick={handleCancel}>
            <Icon icon="cancel" text={t("cancel")} />
          </Button>
        )}
      </Title>
      <Form {...formProps}>
        <MainForm {...childFormProps} />
        {!formData.isPublisher && <PrivacyForm {...childFormProps} />}
        <PublisherScopeForm {...childFormProps} />
        {formData.environment === "production" && (
          <BillingForm {...childFormProps} />
        )}
        <OptOutForm {...childFormProps} />
        <WifiForm {...childFormProps} />
      </Form>
    </>
  );
}
